import { useMemo } from "react";

import { getCompletedVehicles } from "../components/productionDashboard/AssemblyFlow";
import { DismantlingBayType } from "../models/DismantlingBay";
import useProductionDashboardOverview from "./useProductionDashboardOverview";

/**
 * The Production Dashboard's own "Completed Today" figure, derived once so anything else showing a
 * dismantled count lands on exactly the same number.
 *
 * It is not the /stats/range dismantled figure and cannot be: that counts Vehicle.Status changes
 * for a date range, while this is today's allocations sitting at 100% on a line, minus the ones
 * whose triage/depollution timer is still running. The two legitimately disagree, so the only way
 * to match the section is to read the same payload it does.
 *
 * Callers share the underlying query cache with the Production Dashboard (same react-query key),
 * so this costs no extra request when both are in play.
 */
export default function useProductionCompletedToday(bayType: DismantlingBayType) {
  const { overview } = useProductionDashboardOverview(bayType);
  const data = overview.data?.data;

  const bays = useMemo(() => data?.bays ?? [], [data]);
  const inProgressTriages = useMemo(() => data?.inProgressTriages ?? [], [data]);
  const inProgressDepollutions = useMemo(() => data?.inProgressDepollutions ?? [], [data]);

  // Vehicles with an active triage or depollution timer are being worked elsewhere, so they
  // shouldn't be shown sitting in an assembly in the flow (they appear in the in-progress
  // triage/depollution columns instead) and don't count as completed.
  const busyVehicleIds = useMemo(() => {
    const ids = new Set<string>();
    inProgressTriages.forEach((s) => s.vehicleId && ids.add(s.vehicleId));
    inProgressDepollutions.forEach((s) => s.vehicleId && ids.add(s.vehicleId));
    return ids;
  }, [inProgressTriages, inProgressDepollutions]);

  const completedToday = useMemo(
    () => getCompletedVehicles(bays, busyVehicleIds),
    [bays, busyVehicleIds]
  );

  return {
    overview,
    data,
    bays,
    inProgressTriages,
    inProgressDepollutions,
    busyVehicleIds,
    completedToday,
  };
}
